/* global SideDrawer, initLazyImages */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;
      this.previousCheckboxStates = new Map(); // Track previous checkbox states

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      // Set initial filter button state
      this.setInitialFilterButtonState();
      
      // Initialize checkbox states
      this.initializeCheckboxStates();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
        this.drawerFooter = this.querySelector('.js-drawer-footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
        this.sortSection = this.querySelector('.js-sort-section');
        this.sortButton = document.querySelector('.js-toggle-sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
        
        // Add listener for filter button click
        const filterButton = document.querySelector('.js-toggle-filters');
        if (filterButton) {
          filterButton.addEventListener('click', this.handleFilterButtonClick.bind(this));
        }
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
        
        // Add listener for sort button click
        if (this.sortButton) {
          this.sortButton.addEventListener('click', this.handleSortButtonClick.bind(this));
        }
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Resets the drawer view to show filters by default
     */
    resetDrawerView() {
      // Show all filter sections
      const filterSections = this.querySelectorAll('.facets__filters > details-disclosure');
      filterSections.forEach(section => {
        section.style.display = '';
      });
      
      // Hide sort section
      if (this.sortSection) {
        this.sortSection.style.display = 'none';
      }
      
      // Show drawer footer by default
      if (this.drawerFooter) {
        this.drawerFooter.style.display = '';
      }
      
      // Reset drawer header text to show "Filter"
      const drawerTitle = this.querySelector('#facets-title');
      if (drawerTitle) {
        const filterText = drawerTitle.querySelector('.md\\:hidden');
        if (filterText) {
          filterText.textContent = 'Filter';
        }
      }
      
      // Reset button states
      if (this.sortButton) {
        this.sortButton.setAttribute('aria-expanded', 'false');
        this.sortButton.classList.remove('is-active');
      }
      
      const filterButton = document.querySelector('.js-toggle-filters');
      if (filterButton) {
        filterButton.setAttribute('aria-expanded', 'false');
        filterButton.classList.remove('is-active');
      }
      
      // Reinitialize checkbox states when drawer is reset
      this.initializeCheckboxStates();
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      // Check if this is a sort option click for faster response
      const isSortOptionClick = evt.target.classList.contains('js-drawer-sort-by');
      const timeoutDelay = isSortOptionClick ? 50 : 150;
      
      // Check if this is a price filter change
      const isPriceFilterChange = evt.target.id?.includes('price-range');

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        // Check if this is a sort option click (re-check inside timeout)
        const isSortOptionClickInTimeout = evt.target.classList.contains('js-drawer-sort-by');

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Auto-sort by price (low to high) when price filter is applied
          // Only auto-sort if the user hasn't explicitly chosen a different sort option
          if (isPriceFilterChange) {
            const minPrice = searchParams.get('filter.v.price.gte');
            const maxPrice = searchParams.get('filter.v.price.lte');
            
            // If price filter is being applied (has min or max value)
            if (minPrice || maxPrice) {
              // Set sort to price ascending (low to high)
              currentSortBy = 'price-ascending';
              
              // Update the mobile sort options to reflect this change
              this.mobileSortByOptions?.forEach((option) => {
                option.checked = option.value === 'price-ascending';
              });
              
              // Update desktop sort dropdown if it exists
              if (this.desktopSortBy) {
                const selectElement = this.desktopSortBy.querySelector('select');
                if (selectElement) {
                  selectElement.value = 'price-ascending';
                }
              }
            }
          }

          // Set the 'sort_by' parameter.
          searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt, true, isSortOptionClickInTimeout);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filter button.
     * @param {object} evt - Event object.
     */
    handleFilterButtonClick(evt) {
      // On desktop, let the ProductsToolbar handle the toggle behavior
      if (theme.mediaMatches.lg) {
        return; // Don't prevent default, let ProductsToolbar handle it
      }
      
      evt.preventDefault();
      
      // Show all filter sections and hide sort section
      const filterSections = this.querySelectorAll('.facets__filters > details-disclosure');
      filterSections.forEach(section => {
        section.style.display = '';
      });
      
      // Hide sort section
      if (this.sortSection) {
        this.sortSection.style.display = 'none';
      }
      
      // Show drawer footer for filter mode
      if (this.drawerFooter) {
        this.drawerFooter.style.display = '';
      }
      
      // Reset drawer header text to show "Filter"
      const drawerTitle = this.querySelector('#facets-title');
      if (drawerTitle) {
        const filterText = drawerTitle.querySelector('.md\\:hidden');
        if (filterText) {
          filterText.textContent = 'Filter';
        }
      }
      
      // Open the drawer
      this.open();
      
      // Update button state
      const filterButton = document.querySelector('.js-toggle-filters');
      if (filterButton) {
        filterButton.setAttribute('aria-expanded', 'true');
        filterButton.classList.add('is-active');
      }
      
      // Reset sort button state
      if (this.sortButton) {
        this.sortButton.setAttribute('aria-expanded', 'false');
        this.sortButton.classList.remove('is-active');
      }
      
      // Reinitialize checkbox states when drawer is opened for filters
      this.initializeCheckboxStates();
    }

    /**
     * Handles 'click' events on the sort button.
     * @param {object} evt - Event object.
     */
    handleSortButtonClick(evt) {
      evt.preventDefault();
      
      // Hide all filter sections and show only sort section
      const filterSections = this.querySelectorAll('.facets__filters > details-disclosure');
      filterSections.forEach(section => {
        section.style.display = 'none';
      });
      
      // Show sort section
      if (this.sortSection) {
        this.sortSection.style.display = 'block';
      }
      
      // Hide drawer footer for sort mode
      if (this.drawerFooter) {
        this.drawerFooter.style.display = 'none';
      }
      
      // Update drawer header text to show "Sort By"
      const drawerTitle = this.querySelector('#facets-title');
      if (drawerTitle) {
        const sortByText = drawerTitle.querySelector('.md\\:hidden');
        if (sortByText) {
          sortByText.textContent = 'Sort By';
        }
      }
      
      // Open the drawer
      this.open();
      
      // Update button state
      this.sortButton.setAttribute('aria-expanded', 'true');
      this.sortButton.classList.add('is-active');
      
      // Reset filter button state
      const filterButton = document.querySelector('.js-toggle-filters');
      if (filterButton) {
        filterButton.setAttribute('aria-expanded', 'false');
        filterButton.classList.remove('is-active');
      }
    }

    /**
     * Override the close method to reset view
     */
    close() {
      super.close();
      this.resetDrawerView();
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Handle checkbox clicks for multiple selection functionality
      if (target.type === 'checkbox') {
        const checkboxKey = `${target.name}-${target.value}`;
        
        // Update the previous state for this checkbox
        this.previousCheckboxStates.set(checkboxKey, target.checked);
      }

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     * @param {boolean} [isSortOptionClick=false] - Whether this was triggered by a sort option click.
     */
    async applyFilters(searchParams, evt, updateUrl = true, isSortOptionClick = false) {
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // If this was a sort option click, close the drawer automatically
          if (isSortOptionClick) {
            // Reset button states
            if (this.sortButton) {
              this.sortButton.setAttribute('aria-expanded', 'false');
              this.sortButton.classList.remove('is-active');
            }
            
            const filterButton = document.querySelector('.js-toggle-filters');
            if (filterButton) {
              filterButton.setAttribute('aria-expanded', 'false');
              filterButton.classList.remove('is-active');
            }
            
            // Close the drawer
            this.close();
          }

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Update mobile active filters section
          this.updateMobileActiveFilters(tmpl.content);

          // Update filter button state
          this.updateFilterButtonState(tmpl.content);

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();
          
          // Reinitialize checkbox states after filters are applied
          this.initializeCheckboxStates();

          // Reinitialize lazyload images after filters applied.
          if (typeof initLazyImages === 'function') initLazyImages();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error);
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Updates the mobile active filters section with new content.
     * @param {DocumentFragment} tmpl - Template content from the fetch response.
     */
    updateMobileActiveFilters(tmpl) {
      const mobileActiveFilters = document.getElementById('mobile-active-filters');
      if (!mobileActiveFilters) return;

      // Get the new mobile active filters content from the template
      const newMobileActiveFilters = tmpl.getElementById('mobile-active-filters');
      
      if (newMobileActiveFilters) {
        // Update the mobile active filters section
        mobileActiveFilters.innerHTML = newMobileActiveFilters.innerHTML;
        
        // Check if there are any active filters to show/hide the section
        const hasActiveFilters = newMobileActiveFilters.querySelector('.mobile-active-filter-tag');
        if (hasActiveFilters) {
          mobileActiveFilters.style.display = '';
        } else {
          mobileActiveFilters.style.display = 'none';
        }
      } else {
        // If no active filters section in template, hide the section
        mobileActiveFilters.style.display = 'none';
      }
    }

    /**
     * Initialize checkbox states to track their previous checked state.
     */
    initializeCheckboxStates() {
      if (!this.filters) return;
      
      const checkboxes = this.filters.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        const checkboxKey = `${checkbox.name}-${checkbox.value}`;
        this.previousCheckboxStates.set(checkboxKey, checkbox.checked);
      });
    }

    /**
     * Sets the initial filter button state based on current active filters.
     */
    setInitialFilterButtonState() {
      const filterButton = document.querySelector('.js-toggle-filters');
      const activeFilters = document.querySelector('.active-filters');
      const hasActiveFilters = activeFilters && activeFilters.querySelector('.active-filter');
      const filterCountBadge = document.querySelector('.filter-count-badge');
      
      if (filterButton) {
        if (hasActiveFilters) {
          filterButton.classList.add('has-active-filters');
        } else {
          filterButton.classList.remove('has-active-filters');
        }
      }
      
      // Update filter count badge
      if (filterCountBadge) {
        const activeFilterCount = activeFilters ? activeFilters.querySelectorAll('.active-filter').length : 0;
        filterCountBadge.textContent = `(${activeFilterCount})`;
        filterCountBadge.setAttribute('data-filter-count', activeFilterCount);
        
        if (activeFilterCount > 0) {
          filterCountBadge.classList.remove('hidden');
        } else {
          filterCountBadge.classList.add('hidden');
        }
      }
    }

    /**
     * Updates the filter button state based on active filters.
     * @param {string} html - HTML of the fetched page.
     */
    updateFilterButtonState(html) {
      const filterButton = document.querySelector('.js-toggle-filters');
      const activeFilters = html.querySelector('.active-filters');
      const hasActiveFilters = activeFilters && activeFilters.querySelector('.active-filter');
      const filterCountBadge = document.querySelector('.filter-count-badge');
      
      if (filterButton) {
        if (hasActiveFilters) {
          filterButton.classList.add('has-active-filters');
        } else {
          filterButton.classList.remove('has-active-filters');
        }
      }
      
      // Update filter count badge
      if (filterCountBadge) {
        const activeFilterCount = activeFilters ? activeFilters.querySelectorAll('.active-filter').length : 0;
        filterCountBadge.textContent = `(${activeFilterCount})`;
        filterCountBadge.setAttribute('data-filter-count', activeFilterCount);
        
        if (activeFilterCount > 0) {
          filterCountBadge.classList.remove('hidden');
        } else {
          filterCountBadge.classList.add('hidden');
        }
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
